import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, Bell, CreditCard, Shield, HelpCircle, LogOut, ChevronRight, 
  Mail, MapPin, Calendar, Save, X, Edit2, Camera, Phone, Lock, 
  Globe, Heart, Eye, EyeOff, Upload, Trash2, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader,
  WhatsAppBanner, NoireBanner 
} from '@/components/layout/MobileLayout';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Get user settings from localStorage or defaults
const getStoredSettings = () => {
  const stored = localStorage.getItem('exit88_user_settings');
  if (stored) {
    return JSON.parse(stored);
  }
  return {
    notifications: {
      email: true,
      push: true,
      marketing: false,
      eventReminders: true,
      newEvents: true
    },
    privacy: {
      profilePublic: true,
      showLocation: false,
      showActivity: true
    },
    paymentMethods: [
      { id: 'pm-001', type: 'card', brand: 'Visa', last4: '4242', isDefault: true }
    ]
  };
};

const saveSettings = (settings) => {
  localStorage.setItem('exit88_user_settings', JSON.stringify(settings));
};

// Modal/Sheet wrapper - mobile=sheet, desktop=modal
const EditSurface = ({ isOpen, onClose, title, children, isMobile }) => {
  if (!isOpen) return null;

  if (isMobile) {
    // Bottom Sheet for mobile
    return (
      <div className="fixed inset-0 z-50" data-testid="edit-sheet">
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
        <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border rounded-t-2xl animate-in slide-in-from-bottom max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Modal for desktop
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl w-full max-w-md max-h-[85vh] flex flex-col animate-in zoom-in-95">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal for destructive actions
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirm', isDanger = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="confirm-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl w-full max-w-sm p-6 animate-in zoom-in-95">
        <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
        <p className="text-muted-foreground mb-6">{message}</p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 border-border" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            className={cn("flex-1", isDanger ? "bg-destructive hover:bg-destructive/90" : "bg-primary")}
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Settings Menu Item
const SettingsItem = ({ icon: Icon, label, description, value, onClick, danger = false }) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full flex items-center gap-4 p-4 rounded-xl transition-colors text-left",
      danger 
        ? "hover:bg-destructive/10 text-destructive" 
        : "hover:bg-secondary text-white"
    )}
    data-testid={`settings-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className={cn(
      "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
      danger ? "bg-destructive/20" : "bg-secondary"
    )}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium">{label}</p>
      {description && <p className="text-sm text-muted-foreground truncate">{description}</p>}
    </div>
    {value && <span className="text-sm text-muted-foreground mr-2">{value}</span>}
    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
  </button>
);

// Profile Row Item (clickable field)
const ProfileRow = ({ icon: Icon, label, value, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 p-3 hover:bg-secondary/50 rounded-lg transition-colors text-left"
    data-testid={`profile-row-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm text-white truncate">{value || 'Not set'}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
  </button>
);

export function UserSettings() {
  const { user, isAuthenticated, logout, updateProfile } = useAuth();
  const [settings, setSettings] = useState(getStoredSettings());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // Modal states
  const [editModal, setEditModal] = useState({ type: null, isOpen: false });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get user data from localStorage or auth context
  const userData = React.useMemo(() => {
    const stored = localStorage.getItem('exit88_user');
    if (stored) return JSON.parse(stored);
    return user || {
      name: 'Demo User',
      firstName: 'Demo',
      lastName: 'User',
      email: 'demo@example.com',
      phone: '',
      dateOfBirth: '',
      gender: '',
      location: '',
      bio: '',
      username: '',
      avatar: null
    };
  }, [user]);

  const openEditModal = (type, initialData = {}) => {
    setEditForm(initialData);
    setEditModal({ type, isOpen: true });
  };

  const closeEditModal = () => {
    setEditModal({ type: null, isOpen: false });
    setEditForm({});
    setShowPassword(false);
  };

  const saveUserData = (updates) => {
    const newUser = { ...userData, ...updates };
    localStorage.setItem('exit88_user', JSON.stringify(newUser));
    if (updateProfile) updateProfile(newUser);
    toast.success('Saved successfully');
    closeEditModal();
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    window.location.href = '/';
  };

  // Render edit surfaces based on type
  const renderEditContent = () => {
    switch (editModal.type) {
      case 'avatar':
        return (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-primary/20 border-4 border-primary flex items-center justify-center overflow-hidden">
                  {userData.avatar ? (
                    <img src={userData.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-primary" />
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
                // Simulate upload - in production would open file picker
                const mockAvatar = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200';
                saveUserData({ avatar: mockAvatar });
              }}>
                <Upload className="w-4 h-4 mr-2" />
                Upload New Photo
              </Button>
              {userData.avatar && (
                <Button variant="outline" className="w-full border-destructive text-destructive" onClick={() => {
                  saveUserData({ avatar: null });
                }}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Photo
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Recommended: Square image, at least 200x200px
            </p>
          </div>
        );

      case 'name':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">First Name *</label>
              <Input
                value={editForm.firstName || ''}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                placeholder="First name"
                className="bg-secondary border-border"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Last Name *</label>
              <Input
                value={editForm.lastName || ''}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                placeholder="Last name"
                className="bg-secondary border-border"
              />
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              if (!editForm.firstName?.trim()) {
                toast.error('First name is required');
                return;
              }
              saveUserData({
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                name: `${editForm.firstName} ${editForm.lastName || ''}`.trim()
              });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'username':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Username</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                <Input
                  value={editForm.username || ''}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="username"
                  className="bg-secondary border-border pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Only letters, numbers, and underscores allowed
              </p>
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              saveUserData({ username: editForm.username });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Email Address *</label>
              <Input
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="your@email.com"
                className="bg-secondary border-border"
              />
            </div>
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-xs text-yellow-400">
                Email verification will be required. For now, changes save immediately (UI-only demo).
              </p>
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(editForm.email || '')) {
                toast.error('Please enter a valid email address');
                return;
              }
              saveUserData({ email: editForm.email });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'password':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Current Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={editForm.currentPassword || ''}
                  onChange={(e) => setEditForm({ ...editForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  className="bg-secondary border-border pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">New Password *</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={editForm.newPassword || ''}
                onChange={(e) => setEditForm({ ...editForm, newPassword: e.target.value })}
                placeholder="Enter new password"
                className="bg-secondary border-border"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Confirm New Password *</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={editForm.confirmPassword || ''}
                onChange={(e) => setEditForm({ ...editForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                className="bg-secondary border-border"
              />
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-400">
                Password must be at least 8 characters. UI-only demo - no real authentication.
              </p>
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              if ((editForm.newPassword || '').length < 8) {
                toast.error('Password must be at least 8 characters');
                return;
              }
              if (editForm.newPassword !== editForm.confirmPassword) {
                toast.error('Passwords do not match');
                return;
              }
              toast.success('Password updated (simulated)');
              closeEditModal();
            }}>
              <Lock className="w-4 h-4 mr-2" />
              Update Password
            </Button>
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Phone Number</label>
              <Input
                type="tel"
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                className="bg-secondary border-border"
              />
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              saveUserData({ phone: editForm.phone });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'dob':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Date of Birth</label>
              <Input
                type="date"
                value={editForm.dateOfBirth || ''}
                onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
                className="bg-secondary border-border"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Your birthday is used for age-restricted events. Only your age range is shown publicly.
            </p>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              saveUserData({ dateOfBirth: editForm.dateOfBirth });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'gender':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Gender</label>
              <select
                value={editForm.gender || ''}
                onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-white"
              >
                <option value="">Prefer not to say</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
              </select>
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              saveUserData({ gender: editForm.gender });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Location</label>
              <Input
                value={editForm.location || ''}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                placeholder="City, State/Country"
                className="bg-secondary border-border"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Your location helps us show you nearby events.
            </p>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              saveUserData({ location: editForm.location });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'bio':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Bio</label>
              <textarea
                value={editForm.bio || ''}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                className="w-full bg-secondary border border-border rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none h-32"
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {(editForm.bio || '').length}/200 characters
              </p>
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              saveUserData({ bio: editForm.bio });
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-white font-medium">Email Notifications</p>
                <p className="text-xs text-muted-foreground">Receive updates via email</p>
              </div>
              <Switch
                checked={editForm.email ?? true}
                onCheckedChange={(checked) => setEditForm({ ...editForm, email: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-white font-medium">Push Notifications</p>
                <p className="text-xs text-muted-foreground">Browser push notifications</p>
              </div>
              <Switch
                checked={editForm.push ?? true}
                onCheckedChange={(checked) => setEditForm({ ...editForm, push: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-white font-medium">Event Reminders</p>
                <p className="text-xs text-muted-foreground">Get reminded before events start</p>
              </div>
              <Switch
                checked={editForm.eventReminders ?? true}
                onCheckedChange={(checked) => setEditForm({ ...editForm, eventReminders: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-white font-medium">New Events</p>
                <p className="text-xs text-muted-foreground">Notify me about new events</p>
              </div>
              <Switch
                checked={editForm.newEvents ?? true}
                onCheckedChange={(checked) => setEditForm({ ...editForm, newEvents: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-white font-medium">Marketing</p>
                <p className="text-xs text-muted-foreground">Promotional emails and offers</p>
              </div>
              <Switch
                checked={editForm.marketing ?? false}
                onCheckedChange={(checked) => setEditForm({ ...editForm, marketing: checked })}
              />
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              const newSettings = { ...settings, notifications: editForm };
              setSettings(newSettings);
              saveSettings(newSettings);
              toast.success('Saved successfully');
              closeEditModal();
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Manage your saved payment methods.
            </p>
            {settings.paymentMethods.map((pm) => (
              <Card key={pm.id} className="bg-secondary/50 border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-7 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                    <span className="text-white text-[8px] font-bold">{pm.brand}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-mono text-sm">•••• •••• •••• {pm.last4}</p>
                    {pm.isDefault && (
                      <span className="text-xs text-primary">Default</span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            <Button variant="outline" className="w-full border-border" onClick={() => {
              toast.info('Add payment method - UI only demo');
            }}>
              <CreditCard className="w-4 h-4 mr-2" />
              Add Payment Method
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Card details are securely stored. Only last 4 digits are displayed.
            </p>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-white font-medium">Public Profile</p>
                <p className="text-xs text-muted-foreground">Allow others to see your profile</p>
              </div>
              <Switch
                checked={editForm.profilePublic ?? true}
                onCheckedChange={(checked) => setEditForm({ ...editForm, profilePublic: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-white font-medium">Show Location</p>
                <p className="text-xs text-muted-foreground">Display your city on profile</p>
              </div>
              <Switch
                checked={editForm.showLocation ?? false}
                onCheckedChange={(checked) => setEditForm({ ...editForm, showLocation: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-white font-medium">Activity Status</p>
                <p className="text-xs text-muted-foreground">Show when you're online</p>
              </div>
              <Switch
                checked={editForm.showActivity ?? true}
                onCheckedChange={(checked) => setEditForm({ ...editForm, showActivity: checked })}
              />
            </div>
            <Button className="w-full bg-primary text-primary-foreground" onClick={() => {
              const newSettings = { ...settings, privacy: editForm };
              setSettings(newSettings);
              saveSettings(newSettings);
              toast.success('Saved successfully');
              closeEditModal();
            }}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        );

      case 'help':
        return (
          <div className="space-y-4">
            <Card className="bg-secondary/50 border-border p-4 hover:bg-secondary/70 cursor-pointer transition-colors" onClick={() => {
              toast.info('Opening FAQ...');
            }}>
              <p className="text-white font-medium">FAQ</p>
              <p className="text-xs text-muted-foreground">Frequently asked questions</p>
            </Card>
            <Card className="bg-secondary/50 border-border p-4 hover:bg-secondary/70 cursor-pointer transition-colors" onClick={() => {
              toast.info('Opening contact form...');
            }}>
              <p className="text-white font-medium">Contact Support</p>
              <p className="text-xs text-muted-foreground">Get help from our team</p>
            </Card>
            <Card className="bg-secondary/50 border-border p-4 hover:bg-secondary/70 cursor-pointer transition-colors" onClick={() => {
              toast.info('Opening terms...');
            }}>
              <p className="text-white font-medium">Terms of Service</p>
              <p className="text-xs text-muted-foreground">Read our terms</p>
            </Card>
            <Card className="bg-secondary/50 border-border p-4 hover:bg-secondary/70 cursor-pointer transition-colors" onClick={() => {
              toast.info('Opening privacy policy...');
            }}>
              <p className="text-white font-medium">Privacy Policy</p>
              <p className="text-xs text-muted-foreground">How we handle your data</p>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  const getModalTitle = () => {
    const titles = {
      avatar: 'Profile Photo',
      name: 'Edit Name',
      username: 'Edit Username',
      email: 'Edit Email',
      password: 'Change Password',
      phone: 'Edit Phone',
      dob: 'Date of Birth',
      gender: 'Gender',
      location: 'Location',
      bio: 'Edit Bio',
      notifications: 'Notification Preferences',
      payment: 'Payment Methods',
      privacy: 'Privacy & Security',
      help: 'Help & Support'
    };
    return titles[editModal.type] || 'Edit';
  };

  // Profile Card Component
  const ProfileCard = ({ isMobile = false }) => (
    <Card className={`bg-card border-border ${isMobile ? 'p-4 mb-4' : 'p-6 mb-6'}`} data-testid="profile-card">
      {/* Avatar Section */}
      <div className="flex items-start gap-4 mb-4">
        <button 
          className="relative group"
          onClick={() => openEditModal('avatar')}
          data-testid="edit-avatar-btn"
        >
          <div className={`${isMobile ? 'w-16 h-16' : 'w-20 h-20'} rounded-full overflow-hidden border-2 border-primary flex-shrink-0`}>
            {userData.avatar ? (
              <img src={userData.avatar} alt={userData.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-primary flex items-center justify-center">
                <span className={`text-primary-foreground font-bold ${isMobile ? 'text-xl' : 'text-2xl'}`}>
                  {userData.name?.charAt(0) || 'U'}
                </span>
              </div>
            )}
          </div>
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-5 h-5 text-white" />
          </div>
        </button>
        <div className="flex-1 min-w-0">
          <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-white`}>{userData.name}</h2>
          {userData.username && (
            <p className="text-sm text-primary">@{userData.username}</p>
          )}
          <p className="text-sm text-muted-foreground">{userData.email}</p>
        </div>
      </div>

      {/* Profile Fields */}
      <div className="space-y-1 border-t border-border pt-4">
        <ProfileRow 
          icon={User} 
          label="Name" 
          value={userData.name} 
          onClick={() => openEditModal('name', { firstName: userData.firstName, lastName: userData.lastName })}
        />
        <ProfileRow 
          icon={Globe} 
          label="Username" 
          value={userData.username ? `@${userData.username}` : null} 
          onClick={() => openEditModal('username', { username: userData.username })}
        />
        <ProfileRow 
          icon={Mail} 
          label="Email" 
          value={userData.email} 
          onClick={() => openEditModal('email', { email: userData.email })}
        />
        <ProfileRow 
          icon={Lock} 
          label="Password" 
          value="••••••••" 
          onClick={() => openEditModal('password')}
        />
        <ProfileRow 
          icon={Phone} 
          label="Phone" 
          value={userData.phone} 
          onClick={() => openEditModal('phone', { phone: userData.phone })}
        />
        <ProfileRow 
          icon={Calendar} 
          label="Date of Birth" 
          value={userData.dateOfBirth} 
          onClick={() => openEditModal('dob', { dateOfBirth: userData.dateOfBirth })}
        />
        <ProfileRow 
          icon={Heart} 
          label="Gender" 
          value={userData.gender ? userData.gender.charAt(0).toUpperCase() + userData.gender.slice(1) : null} 
          onClick={() => openEditModal('gender', { gender: userData.gender })}
        />
        <ProfileRow 
          icon={MapPin} 
          label="Location" 
          value={userData.location} 
          onClick={() => openEditModal('location', { location: userData.location })}
        />
        <ProfileRow 
          icon={Edit2} 
          label="Bio" 
          value={userData.bio ? (userData.bio.length > 30 ? userData.bio.substring(0, 30) + '...' : userData.bio) : null} 
          onClick={() => openEditModal('bio', { bio: userData.bio })}
        />
      </div>
    </Card>
  );

  const SettingsContent = ({ isMobile }) => (
    <>
      {/* Profile Card */}
      {isAuthenticated && <ProfileCard isMobile={isMobile} />}

      {/* Settings Menu */}
      <Card className="bg-card border-border overflow-hidden mb-4">
        <SettingsItem 
          icon={Bell} 
          label="Notifications" 
          description="Manage notification preferences" 
          onClick={() => openEditModal('notifications', settings.notifications)}
        />
        <SettingsItem 
          icon={CreditCard} 
          label="Payment Methods" 
          description={`${settings.paymentMethods.length} card${settings.paymentMethods.length !== 1 ? 's' : ''} saved`}
          onClick={() => openEditModal('payment')}
        />
        <SettingsItem 
          icon={Shield} 
          label="Privacy & Security" 
          description="Control your privacy settings" 
          onClick={() => openEditModal('privacy', settings.privacy)}
        />
        <SettingsItem 
          icon={HelpCircle} 
          label="Help & Support" 
          description="FAQ, contact us, and more" 
          onClick={() => openEditModal('help')}
        />
      </Card>

      {isAuthenticated && (
        <Card className="bg-card border-border overflow-hidden mb-6">
          <SettingsItem 
            icon={LogOut} 
            label="Log Out" 
            danger 
            onClick={() => setShowLogoutConfirm(true)}
          />
        </Card>
      )}

      {!isAuthenticated && (
        <div className="space-y-3 mb-6">
          <Link to="/login">
            <Button className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              LOG IN
            </Button>
          </Link>
          <Link to="/register">
            <Button variant="outline" className="w-full h-12 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
              SIGN UP
            </Button>
          </Link>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="user-settings">
      {/* Mobile Layout */}
      <div className="lg:hidden">
        <MobileHeader />
        
        <main className="pt-14 pb-24 px-4">
          <div className="mt-4 mb-6">
            <WhatsAppBanner />
          </div>

          <h1 className="text-2xl font-bold text-white mb-4">Settings</h1>

          <SettingsContent isMobile={true} />

          <div className="mt-6">
            <NoireBanner />
          </div>
        </main>

        <MobileBottomNav />
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <DesktopHeader />
        
        <main className="pt-20 pb-12">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="mb-8">
              <WhatsAppBanner />
            </div>

            <h1 className="text-3xl font-bold text-white mb-6">Settings</h1>

            <SettingsContent isMobile={false} />

            <div className="mt-8">
              <NoireBanner />
            </div>
          </div>
        </main>
      </div>

      {/* Edit Surface */}
      <EditSurface
        isOpen={editModal.isOpen}
        onClose={closeEditModal}
        title={getModalTitle()}
        isMobile={isMobile}
      >
        {renderEditContent()}
      </EditSurface>

      {/* Logout Confirmation */}
      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Log Out"
        message="Are you sure you want to log out of your account?"
        confirmText="Log Out"
        isDanger={true}
      />
    </div>
  );
}

export default UserSettings;
